import { useTranslation } from 'react-i18next';

import EmptyState from '@/components/ui/EmptyState';
import SectionHeader from '@/components/ui/SectionHeader';
import { cn } from '@/lib/utils';

interface Shot {
  shot_id: string;
  scene_number: number;
  roll?: string;
  shot_type: string;
  description: string;
  motion_descriptor: string;
  dialog_line_ids?: string[];
  vo_line_ids?: string[];
  duration_seconds?: number;
  estimated_duration_seconds?: number;
  transition_in?: string;
  transition_out?: string;
}

/** Framing → chip colour. Unknown types fall through to a neutral chip. */
const SHOT_TYPE_COLORS: Record<string, string> = {
  establishing: 'bg-blue-500/15 text-blue-400',
  wide: 'bg-blue-500/15 text-blue-400',
  medium: 'bg-green-500/15 text-green-400',
  'close-up': 'bg-amber-500/15 text-amber-400',
  'extreme-close-up': 'bg-red-500/15 text-red-400',
  OTS: 'bg-purple-500/15 text-purple-400',
  POV: 'bg-purple-500/15 text-purple-400',
  tracking: 'bg-cyan-500/15 text-cyan-400',
  insert: 'bg-gray-500/15 text-gray-400',
};

function ShotRow({ shot, t }: { shot: Shot; t: (k: string, o?: Record<string, unknown>) => string }) {
  const audioCount = (shot.dialog_line_ids?.length || 0) + (shot.vo_line_ids?.length || 0);
  const isBroll = shot.roll === 'b_roll';
  const duration = shot.duration_seconds ?? shot.estimated_duration_seconds;

  return (
    <div className="rounded-lg border border-border bg-elevated px-4 py-3.5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11.5px] text-faint">{shot.shot_id}</span>
        <span
          className={cn(
            'rounded px-2 py-0.5 font-mono text-[10px]',
            SHOT_TYPE_COLORS[shot.shot_type] || 'bg-muted text-muted-foreground',
          )}
        >
          {shot.shot_type}
        </span>
        {isBroll && <span className="rounded bg-primary/15 px-2 py-0.5 font-mono text-[10px] text-primary">B-ROLL</span>}
        {audioCount > 0 && <span className="text-[10px] text-faint">🎙 {audioCount}</span>}
        {shot.transition_in && shot.transition_in !== 'cut' && (
          <span className="text-[9px] text-faint">← {shot.transition_in}</span>
        )}
        <span className="ml-auto font-mono text-[11px] text-faint">{duration ? `${Math.round(duration)}s` : '—'}</span>
      </div>
      <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
        {shot.description}
        {shot.motion_descriptor && (
          <span className="text-faint"> ({t('shotlist.camera', { motion: shot.motion_descriptor })})</span>
        )}
      </p>
    </div>
  );
}

/**
 * The board: every shot the crew will create, with framing and duration. This is the
 * plan the render engine is handed — reading it is how a producer catches a wrong
 * shot before any render time is spent on it.
 */
export default function ShotListCard({ shotPlan }: { shotPlan: unknown }) {
  const { t } = useTranslation('production');
  const data = shotPlan as { shots?: Shot[] } | null;
  const shots = data?.shots;

  if (!shots?.length) {
    return <EmptyState title={t('shotlist.emptyTitle')}>{t('shotlist.empty')}</EmptyState>;
  }

  const aRoll = shots.filter((s) => (s.roll || 'a_roll') === 'a_roll');
  const bRoll = shots.filter((s) => s.roll === 'b_roll');

  return (
    <div>
      <SectionHeader
        title={t('step.shotlist')}
        help={t('shotlist.help')}
        subtitle={t('shotlist.count', { count: shots.length })}
        right={
          <div className="flex gap-2">
            <span className="rounded-full border border-border bg-elevated px-2.5 py-1 font-mono text-[10.5px] text-muted-foreground">
              {t('shotlist.aRoll', { count: aRoll.length })}
            </span>
            {bRoll.length > 0 && (
              <span className="rounded-full border border-border bg-elevated px-2.5 py-1 font-mono text-[10.5px] text-muted-foreground">
                {t('shotlist.bRoll', { count: bRoll.length })}
              </span>
            )}
          </div>
        }
      />
      <div className="space-y-3">
        {shots.map((shot) => (
          <ShotRow key={shot.shot_id} shot={shot} t={t} />
        ))}
      </div>
    </div>
  );
}
